import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Stack, Typography, TextField, Button, Alert, CircularProgress, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, Collapse, Snackbar, Chip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import * as XLSX from 'xlsx';
import apiService from '../services/api.service';

interface PickOrderRef {
  docNum: number; cardCode: string; cardName: string; docDueDate: string; uomCode: string; openQty: number;
}
interface PickRow {
  itemCode: string; itemDescription: string; warehouseCode: string;
  byUom: { uomCode: string; openQty: number }[];
  totalInventoryQty: number; inventoryUom: string | null;
  orderCount: number; orders: PickOrderRef[];
}
interface PickData {
  from: string; to: string; orderCount: number; truncated: boolean;
  warehouses: string[]; rows: PickRow[];
}

/** Local calendar date — toISOString is UTC and can be yesterday here. */
function todayIso(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const qty = (n: number) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 3 });
const byUomText = (r: PickRow) => r.byUom.map((u) => `${qty(u.openQty)} ${u.uomCode}`).join(' + ');

function WarehousePickingList() {
  const companyId = useMemo(
    () => Number(localStorage.getItem('selectedCompanyId') || '') || undefined, []);
  const [from, setFrom] = useState(todayIso());
  const [to, setTo] = useState(todayIso());
  const [warehouse, setWarehouse] = useState('');
  const [data, setData] = useState<PickData | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res: any = await apiService.getPickingList(companyId, from, to);
      setData(res.data);
      setOpen({});
    } catch (e: any) {
      setToast(e.response?.data?.message || 'Could not load the picking list');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [companyId, from, to]);

  useEffect(() => { void load(); }, [load]);

  const rows = useMemo(
    () => (data?.rows ?? []).filter((r) => !warehouse || r.warehouseCode === warehouse),
    [data, warehouse]
  );
  // Grouped by warehouse, in the order the server already sorted — the list is
  // meant to be walked one warehouse at a time.
  const grouped = useMemo(() => {
    const g = new Map<string, PickRow[]>();
    for (const r of rows) g.set(r.warehouseCode, [...(g.get(r.warehouseCode) ?? []), r]);
    return Array.from(g.entries());
  }, [rows]);

  const exportExcel = () => {
    if (!data || rows.length === 0) return;
    const pick = XLSX.utils.json_to_sheet(rows.map((r) => ({
      Warehouse: r.warehouseCode,
      'Item Code': r.itemCode,
      Description: r.itemDescription,
      'To Pick': byUomText(r),
      [`Total (inventory unit)`]: r.totalInventoryQty,
      'Inventory Unit': r.inventoryUom || '',
      Orders: r.orderCount,
    })));
    pick['!cols'] = [{ wch: 12 }, { wch: 18 }, { wch: 44 }, { wch: 22 }, { wch: 12 }, { wch: 10 }, { wch: 8 }];
    const detail = XLSX.utils.json_to_sheet(rows.flatMap((r) => r.orders.map((o) => ({
      Warehouse: r.warehouseCode,
      'Item Code': r.itemCode,
      Description: r.itemDescription,
      'SAP Order': o.docNum,
      'Delivery Date': o.docDueDate,
      Customer: o.cardName,
      'Card Code': o.cardCode,
      'Open Qty': o.openQty,
      Unit: o.uomCode,
    }))));
    detail['!cols'] = [{ wch: 12 }, { wch: 18 }, { wch: 40 }, { wch: 12 }, { wch: 12 }, { wch: 36 }, { wch: 14 }, { wch: 9 }, { wch: 8 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, pick, 'Picking List');
    XLSX.utils.book_append_sheet(wb, detail, 'By Order');
    const span = data.from === data.to ? data.from : `${data.from}_to_${data.to}`;
    XLSX.writeFile(wb, `Picking_List_${span}${warehouse ? `_${warehouse}` : ''}.xlsx`);
  };

  if (!companyId) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Alert severity="info">Select a company (top bar) to build a picking list.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }} className="picking-list">
      {/* Print only the list itself: no app chrome, no filter bar, every
          order detail expanded so the printed sheet is complete. */}
      <style>{`
        @media print {
          header, nav, .no-print { display: none !important; }
          .picking-list { padding: 0 !important; }
          .picking-list .MuiCollapse-root { height: auto !important; visibility: visible !important; }
          .picking-list .MuiPaper-root { box-shadow: none !important; border: none !important; }
          .picking-list tr { break-inside: avoid; }
          .picking-list .wh-group { break-before: page; }
          .picking-list .wh-group:first-of-type { break-before: auto; }
        }
      `}</style>

      <Typography variant="h5" component="h1" gutterBottom>Picking List</Typography>

      <Paper className="no-print" elevation={0}
        sx={{ p: { xs: 1.5, md: 2 }, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }} flexWrap="wrap" useFlexGap>
          <TextField label="Delivery from" type="date" size="small" value={from}
            onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="Delivery to" type="date" size="small" value={to}
            onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }}
            error={to < from} helperText={to < from ? 'Earlier than From — a single day is used' : ' '} />
          <TextField select label="Warehouse" size="small" value={warehouse} sx={{ minWidth: 170 }}
            onChange={(e) => setWarehouse(e.target.value)}>
            <MenuItem value="">All warehouses</MenuItem>
            {(data?.warehouses ?? []).map((w) => <MenuItem key={w} value={w}>{w}</MenuItem>)}
          </TextField>
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={() => void load()} disabled={loading}>Run</Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportExcel}
            disabled={!data || rows.length === 0}>Export Excel</Button>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}
            disabled={!data || rows.length === 0}>Print</Button>
        </Stack>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 4 }}>
          <CircularProgress />
          <Typography variant="caption" color="text.secondary">Reading open orders from SAP…</Typography>
        </Box>
      )}

      {!loading && data && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {data.from === data.to ? `Due ${data.from}` : `Due ${data.from} to ${data.to}`}
            {' · '}{data.orderCount} open order{data.orderCount === 1 ? '' : 's'}
            {' · '}{rows.length} item line{rows.length === 1 ? '' : 's'} to pick
            {warehouse ? ` in ${warehouse}` : ''}
          </Typography>

          {data.truncated && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This range has more open orders than one list reads, so it is incomplete. Narrow the delivery dates.
            </Alert>
          )}

          {rows.length === 0 ? (
            <Alert severity="success">Nothing left to pick for this delivery date.</Alert>
          ) : grouped.map(([wh, list]) => (
            <Paper key={wh} className="wh-group" elevation={0}
              sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Stack direction="row" alignItems="baseline" spacing={1} sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Warehouse {wh}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {list.length} item{list.length === 1 ? '' : 's'}
                </Typography>
              </Stack>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 760 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell className="no-print" sx={{ width: 40 }} />
                      <TableCell>Item</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>To pick</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Orders</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {list.map((r) => {
                      const key = `${wh}|${r.itemCode}`;
                      const isOpen = !!open[key];
                      const mixed = r.byUom.length > 1;
                      return (
                        <Fragment key={key}>
                          <TableRow hover>
                            <TableCell className="no-print">
                              <IconButton size="small" aria-label={isOpen ? `Hide orders for ${r.itemCode}` : `Show orders for ${r.itemCode}`}
                                onClick={() => setOpen((p) => ({ ...p, [key]: !isOpen }))}>
                                {isOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{r.itemCode}</TableCell>
                            <TableCell>{r.itemDescription}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                              {byUomText(r)}
                              {mixed && <Chip size="small" label="mixed units" sx={{ ml: 1 }} />}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                              {qty(r.totalInventoryQty)} {r.inventoryUom || ''}
                            </TableCell>
                            <TableCell align="right">{r.orderCount}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={6} sx={{ py: 0, borderBottom: isOpen ? undefined : 'none' }}>
                              <Collapse in={isOpen} timeout="auto" unmountOnExit={false}>
                                <Table size="small" sx={{ my: 1 }}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>SAP order</TableCell>
                                      <TableCell>Delivery</TableCell>
                                      <TableCell>Customer</TableCell>
                                      <TableCell align="right">Open qty</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {r.orders.map((o, i) => (
                                      <TableRow key={`${o.docNum}-${i}`}>
                                        <TableCell>{o.docNum}</TableCell>
                                        <TableCell>{o.docDueDate}</TableCell>
                                        <TableCell>{o.cardName} ({o.cardCode})</TableCell>
                                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                          {qty(o.openQty)} {o.uomCode}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          ))}
        </>
      )}

      <Snackbar open={!!toast} autoHideDuration={6000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
}

export default WarehousePickingList;
